// src/routes/AppRouter.jsx
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout, setUser } from "../store/authSlice";
import api from "../redux/apis/api";
import { normalizeRole } from "../utils/rbacHelper";
import { markAuthPresence } from "../utils/authPresence";

import Signup from "../components/Signup";
import Login from "../components/Login";
import Profile from "../components/Profile";
import AdminDashboard from "../components/AdminDashboard";
import IndividualDashboard from "../components/IndividualDashboard";
import ViewerDashboard from "../components/ViewerDashboard";
import ProtocolManager from "../components/ProtocolManager";
import CreditsTransfer from "../components/admin/credits/CreditsTranfer";
import MessageManagement from "../components/admin/messagemanagement/MessageManagement";
import GroupAdminDashboard from "../components/groupadmin/GroupAdminDashboard";
import NotificationsPage from "../components/admin/credits/NotificationsPage";

// ✅ NEW IMPORT - Dashboard Component
import Dashboard from "../components/admin/Dashboard";

import ProtectedRoute from "./ProtectedRoute";
import AuthRedirect from "./AuthRedirect";
import TaskManagement from "../components/employee/TaskManagement";
import MyTasks from "../components/employee/MyTasks";
import GroupDetail from "../components/groups/GroupDetail";
import CreateGroup from "../components/groups/CreateGroup";
import GroupsList from "../components/groups/GroupsList";
import AddDevices from "../components/groups/AddDevices";
import ReportsPortal from "../components/reports/ReportsPortal";
import ProtocolManagement from "../components/protocol/Protocolmanagement";
import EditGroup from "../components/groups/EditGroup";
import LogisticsTabsPage from "../components/LogisticsDashboard";
import DeviceControlWrapper from "../components/admin/devices/device/DeviceControlWrapper";
import InventoryTodo from "../components/inventory/InventoryTodo";
import CameraControlPortal from "../components/cameraV2/CameraControlPortal";
import PublicLiveTrackingPage from "../components/public/PublicLiveTrackingPage";
import { LIVE_MAP_PATH } from "./dashboardNavigation";

function DashboardRouteEntry({ renderAdminDashboard, renderDashboard }) {
  const location = useLocation();
  const hasTab = new URLSearchParams(location.search).has("tab");
  return hasTab ? renderAdminDashboard() : renderDashboard();
}

function LegacyAdminDashboardRedirect() {
  const location = useLocation();
  return <Navigate to={`/dashboard${location.search || ""}${location.hash || ""}`} replace />;
}

export default function AppRouter() {
  const dispatch = useDispatch();

  const token = useSelector((s) => s.auth.token);
  const currentUser = useSelector((s) => s.auth.user);

useEffect(() => {
  if (!token) return undefined;

  // A previous account's profile request can finish after Login As (or a new
  // manual login) and overwrite the freshly authenticated user. Abort that
  // request and ignore its result when the token changes.
  const controller = new AbortController();
  let active = true;

  api
    .get("/auth/profile", { signal: controller.signal })
    .then((res) => {
      if (active) dispatch(setUser(res.data));
    })
    .catch((error) => {
      if (!active || error?.code === "ERR_CANCELED") return;
      if (error?.response?.status === 401) {
        dispatch(logout());
      }
    });

  return () => {
    active = false;
    controller.abort();
  };
}, [token, dispatch]);

useEffect(() => {
  if (!token) return undefined;

  const markOfflineOnExit = () => {
    markAuthPresence(token, false, true);
  };

  window.addEventListener("pagehide", markOfflineOnExit);
  window.addEventListener("beforeunload", markOfflineOnExit);

  return () => {
    window.removeEventListener("pagehide", markOfflineOnExit);
    window.removeEventListener("beforeunload", markOfflineOnExit);
  };
}, [token]);

  const SUPER_ADMIN_ONLY = ["admin"];
  const MESSAGE_ACCESS_ROLES = ["admin", "manager", "hr", "office_emp", "sub_admin", "group_admin", "dealer"];
  const CREDITS = ["admin", "manager", "hr", "office_emp", "sub_admin", "group_admin"];
  const TASK_MANAGERS = ["admin", "manager", "hr", "office_emp"];
  const FIELD_WORKERS = ["field_emp"];
  const ALL_USERS_STAFF = [
    "admin",
    "manager",
    "hr",
    "field_emp",
    "office_emp",
    "sub_admin",
    "group_admin",
    "client",
    "user",
    "viewer",
  ];
  const GROUP_ACCESS_ROLES = [
    "admin",
    "manager",
    "office_emp",
    "field_emp",
    "sub_admin",
    "group_admin",
    "client",
    "user",
  ];

  const viewerAtRoot =
    currentUser && normalizeRole(currentUser?.role) === "viewer";
  const authSessionKey = `${String(currentUser?._id || currentUser?.id || "anonymous")}:${String(token || "")}`;

  const renderAdminDashboard = () => (
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <AdminDashboard key={`admin:${authSessionKey}`} />
    </ProtectedRoute>
  );

  const renderDashboard = () => (
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <Dashboard key={`dashboard:${authSessionKey}`} />
    </ProtectedRoute>
  );

  const renderLiveMapPage = () => (
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <AdminDashboard activeTab="map" />
    </ProtectedRoute>
  );
  return (
    <>
      {/* ✅ Navbar removed from here (global in App.jsx now) */}

      <Routes>
        <Route
  path="/"
  element={
    currentUser
      ? viewerAtRoot
        ? renderAdminDashboard()
        : <Navigate to="/dashboard" replace />
      : <Login />
  }
/>
        <Route path="/live-tracking" element={<PublicLiveTrackingPage />} />

        {/* AuthRedirect prevents logged users from opening login/signup */}
        <Route
          path="/login"
          element={
            <AuthRedirect>
              <Login />
            </AuthRedirect>
          }
        />
        <Route
          path="/signup"
          element={
            <AuthRedirect>
              <Signup />
            </AuthRedirect>
          }
        />

        {/* ✅ Dashboard Page */}
        <Route
          path="/dashboard"
          element={
            <DashboardRouteEntry
              renderAdminDashboard={renderAdminDashboard}
              renderDashboard={renderDashboard}
            />
          }
        />

        {/* ✅ Device control */}
        <Route
          path="/devices/:id/control"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <DeviceControlWrapper />
            </ProtectedRoute>
          }
        />

        

        <Route
          path="/details"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <LogisticsTabsPage />
            </ProtectedRoute>
          }
        />

        {/* 🔒 Notifications Page */}
        <Route
          path="/notifications"
          element={
            <ProtectedRoute
              requiredRoles={GROUP_ACCESS_ROLES}
              requiredPermissions={["messageManagement"]}
            >
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
  path="/camera-portal"
  element={
    <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
      <CameraControlPortal />
    </ProtectedRoute>
  }
/>

        {/* 🔒 Task Management */}
        <Route
          path="/task-management"
          element={
            <ProtectedRoute
              requiredRoles={TASK_MANAGERS}
              requiredAnyPermission={["createTask", "assignTask", "viewAllTasks"]}
            >
              <TaskManagement />
            </ProtectedRoute>
          }
        />

        {/* 🔒 My Tasks */}
        <Route
          path="/my-tasks"
          element={
            <ProtectedRoute
              requiredRoles={FIELD_WORKERS}
              requiredPermissions={["viewMyTasks"]}
            >
              <MyTasks />
            </ProtectedRoute>
          }
        />

        <Route
          path="/credits"
          element={
            <ProtectedRoute requiredRoles={CREDITS}>
              <CreditsTransfer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/message"
          element={
            <ProtectedRoute
              requiredRoles={MESSAGE_ACCESS_ROLES}
              requiredPermissions={["messageManagement"]}
            >
              <MessageManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin-dashboard"
          element={
            <LegacyAdminDashboardRedirect />
          }
        />

        <Route
          path={LIVE_MAP_PATH}
          element={renderLiveMapPage()}
        />

        <Route
          path="/protocols"
          element={
            <ProtectedRoute
              requiredRoles={["admin", "manager", "hr", "office_emp", "sub_admin", "group_admin"]}
              requiredPermissions={["viewProtocols"]}
            >
              <ProtocolManagement />
            </ProtectedRoute>
          }
        />

        {/* 🔒 GROUP MANAGEMENT */}
        <Route
          path="/groups"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupsList />
            </ProtectedRoute>
          }
        />

        <Route
          path="/inventory"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <InventoryTodo />
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:id/edit"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <EditGroup />
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/create"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <CreateGroup />
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/edit/:id"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <CreateGroup />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports"
          element={
            <ProtectedRoute requiredRoles={ALL_USERS_STAFF}>
              <ReportsPortal />
            </ProtectedRoute>
          }
        />



        <Route
          path="/groups/:id"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <GroupDetail />
            </ProtectedRoute>
          }
        />

        <Route
          path="/groups/:id/add-devices"
          element={
            <ProtectedRoute requiredRoles={GROUP_ACCESS_ROLES}>
              <AddDevices />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
